import { QueryKey } from "@tanstack/react-query";

import type {
	ExportReviewCycleReviewReportsDetailsBody,
	ExportReviewCycleReviewReportsDetailsResponseJson,
	GetReviewCycleDetailsResponseFrontend,
	GetReviewCycleDetailsResponseJson,
	GetReviewCycleSubjectsResponseFrontend,
	PostReviewCycleCalculateBodyFrontend,
	ReviewDetailsFrontend,
} from "@performyard/service-contracts";
import { JSON_STREAM_DELIMITER } from "@performyard/utils-isomorphic";

import { queryClient } from "../../../queryClient";
import { internalRequest, internalStreamRequest } from "./internal-base-request";

export const calculateReviewCycleStatus = async (reviewCycleId: string) => {
	const body: PostReviewCycleCalculateBodyFrontend = {
		reviewCycleId,
		actions: ["status"],
	};

	const [response] = await internalRequest("/v1/review-cycle/calculate", {
		method: "POST",
		body: JSON.stringify(body),
	});

	return response;
};

export const reviewCycleSubjects = async (reviewCycleId: string) => {
	const [response] = await internalRequest<GetReviewCycleSubjectsResponseFrontend>(
		`/v1/review-cycle/${reviewCycleId}/subjects`,
		{
			method: "GET",
		}
	);

	return response;
};

// Track active streams per review cycle to prevent concurrent streams
const activeStreams = new Map<string, AbortController>();

export const reviewCycleReviewsDetailsStream = async (
	reviewCycleId: string,
	queryKey: QueryKey,
	onChunk?: (chunk: { data: ReviewDetailsFrontend[] }) => void
) => {
	// Abort any existing stream for this review cycle before starting a new one
	const existingController = activeStreams.get(reviewCycleId);
	if (existingController) {
		existingController.abort();
		activeStreams.delete(reviewCycleId);
	}

	// Create a new abort controller for this stream
	const abortController = new AbortController();
	activeStreams.set(reviewCycleId, abortController);

	// Initialize cache to empty array
	queryClient.setQueryData<ReviewDetailsFrontend[]>(queryKey, []);

	try {
		const delimiter = JSON_STREAM_DELIMITER;
		const response = await internalStreamRequest(`/v1/review-cycle/${reviewCycleId}/reviews/details`, {
			method: "GET",
			signal: abortController.signal,
		});

		if (response.status !== 200) {
			throw new Error(`Failed to get review cycle reviews details: ${response.statusText}`);
		}

		const reader = response.body?.getReader();
		if (!reader) {
			throw new Error("Failed to get review cycle reviews details: No reader");
		}

		// Process stream synchronously within the same promise
		const decoder = new TextDecoder("utf-8");
		let hasMore = true;
		let incomplete = "";

		while (hasMore && !abortController.signal.aborted) {
			// Read chunk
			const { done, value } = await reader.read();

			let chunk = decoder.decode(value, { stream: true });

			// If there is an incomplete JSON string, append the current chunk to it
			if (incomplete !== "") {
				chunk = incomplete + chunk;
				incomplete = "";
			}

			// Split the chunk into serialized JSON objects
			const data = chunk.split(delimiter);

			const parsedData: ReviewDetailsFrontend[] = [];
			// Iterate data and attempt to parse each item as JSON
			for (const item of data) {
				if (item === "") {
					continue;
				}
				// Attempt to parse the JSON
				try {
					const parsed = JSON.parse(item);

					parsed.reviewDueDate = parsed.reviewDueDate ? new Date(parsed.reviewDueDate) : null;
					parsed.completeDate = parsed.completeDate ? new Date(parsed.completeDate) : null;

					parsedData.push(parsed);
				} catch (error) {
					// If the JSON is invalid, store the incomplete item
					incomplete += item;
				}
			}

			hasMore = !done;

			// Only update cache if not aborted
			if (!abortController.signal.aborted && parsedData.length > 0) {
				// Call the callback to update the UI with the new chunk
				onChunk?.({ data: parsedData });
				// Save chunk to result
				queryClient.setQueryData(queryKey, (current: ReviewDetailsFrontend[]) => {
					return current ? [...current, ...parsedData] : parsedData;
				});
			}
		}

		// Return the final accumulated result
		return queryClient.getQueryData<ReviewDetailsFrontend[]>(queryKey) ?? [];
	} catch (error: any) {
		// Don't throw on abort - this is expected behavior
		if (error.name === "AbortError") {
			return [];
		}
		throw error;
	} finally {
		// Clean up the active stream tracking
		activeStreams.delete(reviewCycleId);
	}
};

export const deleteReviewFromReviewCycle = async (reviewCycleId: string, reviewId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/reviews/${reviewId}`, {
		method: "DELETE",
	});

	return response;
};

export const getReviewCycleDetails = async (reviewCycleID: string) => {
	const [data] = await internalRequest<GetReviewCycleDetailsResponseJson>(
		`/v1/review-cycle/${reviewCycleID}/details`
	);

	let parsedAutoflow: GetReviewCycleDetailsResponseFrontend["autoflow"] | null = null;
	if (data.autoflow?.trigger?.type === "scheduled") {
		// Convert strings to dates
		parsedAutoflow = {
			...data.autoflow,
			createdAt: data.autoflow.createdAt ? new Date(data.autoflow.createdAt) : undefined,
			deletedAt: data.autoflow.deletedAt ? new Date(data.autoflow.deletedAt) : undefined,
			updatedAt: data.autoflow.updatedAt ? new Date(data.autoflow.updatedAt) : undefined,
			trigger: {
				...data.autoflow.trigger,
				scheduled: {
					...data.autoflow.trigger.scheduled,
					inauguralTimeDate: data.autoflow.trigger.scheduled.inauguralTimeDate
						? new Date(data.autoflow.trigger.scheduled.inauguralTimeDate)
						: undefined,
					startTimeDate: data.autoflow.trigger.scheduled.startTimeDate
						? new Date(data.autoflow.trigger.scheduled.startTimeDate)
						: undefined,
					endTimeDate: data.autoflow.trigger.scheduled.endTimeDate
						? new Date(data.autoflow.trigger.scheduled.endTimeDate)
						: undefined,
				},
			},
		};
	}

	const parsedResp: GetReviewCycleDetailsResponseFrontend = {
		...data,
		startDate: new Date(data.startDate),
		dueDate: data.dueDate ? new Date(data.dueDate) : null,
		autoflow: parsedAutoflow,
	};

	return parsedResp;
};

export const closeReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/close`, {
		method: "POST",
	});

	return response;
};

export const bulkDeactivateEasyAddFormsForReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/easy-add-bulk-deactivate`, {
		method: "POST",
	});

	return response;
};

export const bulkApproveEasyAddFormsForReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/easy-add-bulk-approve`, {
		method: "POST",
	});

	return response;
};

export const deleteReviewCycle = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}`, {
		method: "DELETE",
	});

	return response;
};

export const downloadGoalDiscussionCsv = async (reviewCycleId: string) => {
	const [response] = await internalRequest(`/v1/review-cycle/${reviewCycleId}/goal-discussion-csv-download`, {
		method: "GET",
	});

	return response;
};

export const getGoalDiscussionCsvDownloadStatus = async (jobId: string) => {
	const [response] = await internalRequest(`/v1/async-job/${jobId}/status`, {
		method: "GET",
	});

	return response;
};

export const exportReviewCycleReviewReportsDetailsCsv = async (
	reviewCycleId: string,
	body: ExportReviewCycleReviewReportsDetailsBody
) => {
	const [response] = await internalRequest<ExportReviewCycleReviewReportsDetailsResponseJson>(
		`/v1/review-cycle/${reviewCycleId}/review-reports/details/export-csv`,
		{
			method: "POST",
			body: JSON.stringify(body),
		}
	);

	return response;
};
